import { db, FirebaseTimestamp, FirebaseFieldValue } from '@/js/external-js/config/main_config';
import { doc, getDoc, setDoc, updateDoc, arrayUnion, serverTimestamp } from 'firebase/firestore';

export interface ScopeData {
  id?: string;
  heading: string;
  text: Record<string, string>;
  updatedBy?: string;
  updatedAt?: string;
  history?: ScopeHistory[];
}

export interface ScopeHistory {
  timestamp: any; // Firestore Timestamp or string
  description: string;
  updatedBy: string;
}

const SCOPE_DOC_PATH = 'ISO27001keyDocuments/ismsScope';

/**
 * Fetches all scope items for a company
 * @param tenantId - The tenant ID
 * @returns Promise with the scope data
 */
export async function getAllScopeItems(tenantId: string): Promise<ScopeData> {
  console.log('[scopeService] getAllScopeItems called with:', tenantId);
  
  try {
    const docPath = `customers/${tenantId}/product/policyPackages/${SCOPE_DOC_PATH}`;
    const docRef = doc(db, docPath);
    const docSnap = await getDoc(docRef);
    
    if (docSnap.exists) {
      return {
        id: docSnap.id,
        ...docSnap.data().content.field6,
      } as ScopeData;
    }
    
    // Return default scope data if no document exists
    return {
      heading: 'MSG Scope',
      text: {},
    };
  } catch (error) {
    console.error('[scopeService] Error getting scope data:', error);
    throw error;
  }
}

/**
 * Creates a new scope item
 * @param tenantId - The tenant ID
 * @param data - The scope data to create
 * @returns Promise that resolves when the scope data is created
 */
export async function createScopeItem(tenantId: string, data: ScopeData): Promise<void> {
  console.log('[scopeService] createScopeItem called with:', { tenantId, data });
  
  try {
    const timestamp = new Date().toISOString();
    const docPath = `customers/${tenantId}/product/policyPackages/${SCOPE_DOC_PATH}`;
    const docRef = doc(db, docPath);
    
    await setDoc(docRef, {
      content: {
        field6: {
          ...data,
          updatedAt: timestamp,
        },
      },
      updatedAt: timestamp,
    }, { merge: true });
  } catch (error) {
    console.error('[scopeService] Error saving scope data:', error);
    throw error;
  }
}

/**
 * Updates an existing scope item
 * @param tenantId - The tenant ID
 * @param id - The ID of the scope item to update
 * @param updates - The updated scope data
 * @returns Promise that resolves when the scope data is updated
 */
export async function updateScopeItem(tenantId: string, id: string, updates: ScopeData): Promise<void> {
  console.log('[scopeService] updateScopeItem called with:', { tenantId, id, updates });
  
  try {
    const docPath = `customers/${tenantId}/product/policyPackages/${SCOPE_DOC_PATH}`;
    const docRef = doc(db, docPath);
    const timestamp = new Date().toISOString();

    await setDoc(docRef, {
      content: {
        field6: {
          ...updates,
          updatedAt: timestamp,
        },
      },
      updatedAt: timestamp,
    }, { merge: true });
  } catch (error) {
    console.error('[scopeService] Error updating scope item:', error);
    throw error;
  }
}

/**
 * Deletes a scope item
 * @param companyID - The company ID
 * @param scopeID - The ID of the scope item to delete
 * @returns Promise that resolves when the scope item is deleted
 */
export async function deleteScopeItem(tenantId: string, scopeID: string): Promise<void> {
  console.log('[scopeService] deleteScopeItem called with:', { tenantId, scopeID });
  
  try {
    const docPath = `customers/${tenantId}/product/policyPackages/${SCOPE_DOC_PATH}`;
    const docRef = doc(db, docPath);
    
    const updateData = {
      content: {
        field6: {
          [scopeID]: FirebaseFieldValue.delete()
        }
      }
    };

    await updateDoc(docRef, updateData);
    console.log('[scopeService] Deleted scope item:', scopeID);
  } catch (error) {
    console.error('[scopeService] Error deleting scope item:', error);
    throw error;
  }
}

export default {
  getAllScopeItems,
  createScopeItem,
  updateScopeItem,
  deleteScopeItem
};
